import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { axiosInstance } from "../../../utils/networking";
import { fmt } from "./useReportFilter";

const MAGENTA = "#9E1F63FF";

export interface DrilldownRow {
  case_id: string;
  firmName: string;
  consultantName: string;
  date: string;
  detail: string;
}

export interface DrilldownTarget {
  /** Metric id, matching the catalog / GET /reports/metrics/:id/rows. */
  id: string;
  label: string;
  /** Optional consultant scope for this particular number (a table column). */
  consultant?: string;
  consultantLabel?: string;
}

export interface DrilldownDrawerProps {
  target: DrilldownTarget | null;
  start: Dayjs | null;
  end: Dayjs | null;
  /** Page-level consultant filter, used when the target has none of its own. */
  consultant?: string;
  onClose: () => void;
}

/**
 * Right-side drawer listing the underlying records behind any metric number.
 * Every number in the tiles and tables opens this.
 */
const DrilldownDrawer: React.FC<DrilldownDrawerProps> = ({
  target,
  start,
  end,
  consultant,
  onClose,
}) => {
  const [rows, setRows] = useState<DrilldownRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scope = target?.consultant || consultant || "";
  const metricId = target?.id || "";

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);
    const params: { [k: string]: string } = {};
    if (start) params.start = fmt(start);
    if (end) params.end = fmt(end);
    if (scope) params.consultant = scope;
    axiosInstance
      .get(`/reports/metrics/${encodeURIComponent(metricId)}/rows`, { params })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data?.rows || []);
        setTotal(
          typeof res.data?.total === "number"
            ? res.data.total
            : (res.data?.rows || []).length,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(
          status === 404
            ? "Drilldown is not available for this metric yet."
            : err?.response?.data?.message ||
                err?.message ||
                "Failed to load records.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricId, scope, fmt(start), fmt(end), Boolean(target)]);

  const rangeLabel = `${start ? start.format("MMM D, YYYY") : "Beginning"} – ${
    end ? end.format("MMM D, YYYY") : "Today"
  }`;

  return (
    <Drawer
      anchor="right"
      open={Boolean(target)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c2c34" }}>
              {target?.label || "Records"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#8a8a93" }}>
              {rangeLabel}
              {target?.consultantLabel ? ` · ${target.consultantLabel}` : ""}
              {!loading && !error ? ` · ${total.toLocaleString()} records` : ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <Divider />

      <Box sx={{ p: 2, overflow: "auto" }}>
        {loading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={24} sx={{ color: MAGENTA }} />
          </Stack>
        )}
        {error && <Alert severity="warning">{error}</Alert>}
        {!loading && !error && rows.length === 0 && (
          <Typography sx={{ color: "#8a8a93", py: 4, textAlign: "center" }}>
            No records in this range.
          </Typography>
        )}
        {!loading && !error && rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Business</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Consultant</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`${row.case_id}-${index}`} hover>
                  <TableCell>
                    {row.case_id ? (
                      <Link
                        to={`/case?id=${row.case_id}`}
                        style={{
                          color: MAGENTA,
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        {row.firmName || "Unknown Business"}
                      </Link>
                    ) : (
                      row.firmName || "Unknown Business"
                    )}
                  </TableCell>
                  <TableCell>{row.consultantName || "—"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {row.date ? dayjs(row.date).format("MMM D, YYYY") : "—"}
                  </TableCell>
                  <TableCell>{row.detail || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Drawer>
  );
};

export default DrilldownDrawer;
